import apiRoutes from "@/src/apiRoutes";
import { Button } from "@/src/components/Button/Button";
import InputComponent from "@/src/components/Input/Input";
import AppendInput from "@/src/components/PhoneInput/AppendInput";
import { Label, textVariants } from "@/src/components/Text/Text";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/src/components/shad/select";
import useUrlState from "@/src/hooks/use-url-state";
import { useWalletData } from "@/src/hooks/useCoinData";
import api from "@/src/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import TransferVertical from "@/src/svg/transferVertical";
import { useTheme } from "@/src/context/themeContext";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface Props {
  coin?: WalletDataInfo | undefined;
}

const transferState = z.object({
  from: z.string().min(1, { message: "This field is required" }),
  amount: z.number(),
  type: z.string(),
  coin: z.number(),
});

const Transfer = ({ coin }: Props) => {
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.wallet.transfer.${key}`);
  const { theme } = useTheme();
  type transferType = z.infer<typeof transferState>;
  const { walletData, isLoading } = useWalletData();
  const [, setOpen] = useUrlState("modal");

  const queryClient = useQueryClient();

  const {
    setValue,
    register,
    watch,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<transferType>({
    resolver: zodResolver(transferState),
    defaultValues: {
      coin: coin?.coin__id,
      type: "fiat",
    },
  });

  const confirmTransfer = async () => {
    const res = await api.post(apiRoutes.transfer, {
      ...getValues(),
      max: false,
    });
    return res.data;
  };

  const { mutate, isLoading: mutateLoad } = useMutation({
    mutationFn: confirmTransfer,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data?.info);
        setOpen(undefined);
        queryClient.invalidateQueries({ queryKey: ["wallet_data"] });
      } else {
        toast.warning(data?.info);
      }
    },
  });

  const submit: SubmitHandler<transferType> = () => {
    mutate();
  };
  const selectedCoin = walletData?.wallets.find(
    (item) => item?.coin__id === watch("coin")
  );

  const handleMax = () => {
    if (watch("type") === "crypto") {
      setValue("amount", Number(selectedCoin?.crypto_balance));
    } else {
      setValue("amount", Number(selectedCoin?.fiat_balance));
    }
  };

  if (isLoading) return null;

  return (
    <form onSubmit={handleSubmit(submit)} className="grid gap-4">
      <div>
        <Label>{trans("form.coin.label")}</Label>
        <Select
          defaultValue={coin?.coin__id.toString()}
          onValueChange={(val) => setValue("coin", Number(val))}
        >
          <SelectTrigger>
            <SelectValue placeholder={trans("form.coin.placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {walletData?.wallets?.map((coin) => (
              <SelectItem
                key={coin?.coin__id}
                value={coin?.coin__id.toString()}
              >
                <div className="flex items-center gap-1">
                  <img
                    src={coin?.image}
                    className="h-5 w-5"
                    alt={coin?.ticker}
                  />
                  <span>{coin?.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <Label>{trans("form.from.label")}</Label>
          <Select onValueChange={(val) => setValue("from", val)}>
            <SelectTrigger>
              <SelectValue placeholder={trans("form.from.placeholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="funding">P2P Trading</SelectItem>
              <SelectItem value="spot">Spot</SelectItem>
            </SelectContent>
          </Select>
          {errors && (
            <span className="text-red-500 text-xs font-medium">
              {errors.from?.message}
            </span>
          )}
        </div>
        <div>
          <InputComponent
            disabled={isEmpty(watch("from"))}
            label={trans("form.to.label")}
            value={watch("from") === "funding" ? "Spot" : "P2P Trading"}
          />
        </div>
      </div>
      <div className="flex  gap-3">
        <AppendInput
          label={trans("form.amount.label")}
          {...register("amount", { required: true, valueAsNumber: true })}
          type="number"
          errors={errors.amount?.message}
          right={
            selectedCoin && (
              <div className="p-2">
                <span
                  onClick={handleMax}
                  className={cn(
                    textVariants({ variant: "link", font: "medium" }),
                    "cursor-pointer"
                  )}
                >
                  {trans("form.max")}
                </span>
              </div>
            )
          }
        />
        <div className="mt-6">
          <span
            // disabled={isEmpty(watch("coin"))}
            onClick={() =>
              setValue("type", watch("type") === "fiat" ? "crypto" : "fiat")
            }
            className=" flex cursor-pointer items-center bg-white dark:bg-grey-600 rounded-md border border-input disabled:cursor-not-allowed px-2 py-0.5"
          >
            <TransferVertical
              color={theme === "dark" ? "white" : undefined}
              height={24}
              width={24}
            />
            {/* <div className="uppercase font-bold text-gray-600 dark:text-white mr-2">
              {watch("type") === "fiat" ? "USD" : coin?.ticker}
            </div> */}
            {selectedCoin?.ticker ?? coin?.ticker ? (
              <div className="grid">
                <span className="uppercase font-bold text-gray-600 dark:text-white  text-sm">
                  {watch("type") === "fiat"
                    ? "USD"
                    : selectedCoin?.ticker ?? coin?.ticker}
                </span>
                <span className="uppercase  text-gray-600 dark:text-white text-end  text-[11px]">
                  {watch("type") === "fiat"
                    ? selectedCoin?.ticker ?? coin?.ticker
                    : "USD"}
                </span>
              </div>
            ) : (
              <div className="py-1.5">
                <span className="uppercase font-bold text-gray-600 dark:text-white  text-sm ">
                  {watch("type") === "fiat"
                    ? "USD"
                    : selectedCoin?.ticker ?? coin?.ticker}
                </span>
              </div>
            )}
          </span>
        </div>
      </div>
      <Button type="submit" loading={mutateLoad} className="mt-5 py-3">
        {trans("form.confirmButton")}
      </Button>
    </form>
  );
};

export default Transfer;
